import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Bike, Clock, User, CircleStop, Phone } from "lucide-react";
import { RentalWithRelations } from "@/types/models";
import { format, differenceInSeconds } from "date-fns";
import { useToast } from "@/components/ui/use-toast";
import { useMutation, useQueryClient, useQuery } from "@tanstack/react-query";
import { completeRental, getCurrentExchangeRate } from "@/integrations/supabase/services";
import RentalCompletionModal from "./RentalCompletionModal";

interface ActiveRentalCardProps {
  rental: RentalWithRelations;
}

const ActiveRentalCard = ({ rental }: ActiveRentalCardProps) => {
  const [timeRemaining, setTimeRemaining] = useState<number>(0);
  const [isOvertime, setIsOvertime] = useState(false);
  const [showCompletionModal, setShowCompletionModal] = useState(false);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data: exchangeRate } = useQuery({
    queryKey: ['currentExchangeRate'],
    queryFn: getCurrentExchangeRate,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });

  const completeMutation = useMutation({
    mutationFn: completeRental,
    onSuccess: () => {
      toast({
        title: "¡Éxito!",
        description: "Alquiler finalizado correctamente.",
      });
      queryClient.invalidateQueries({ queryKey: ['active-rentals'] });
      queryClient.invalidateQueries({ queryKey: ['rentals'] });
      setShowCompletionModal(false);
    },
    onError: (error) => {
      console.error("Error completing rental:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Error al finalizar el alquiler. Intenta nuevamente.",
      });
    },
  });

  useEffect(() => {
    const calculateTimeRemaining = () => {
      if (rental.expected_return_time) {
        const now = new Date();
        const returnTime = new Date(rental.expected_return_time);
        const secondsDiff = differenceInSeconds(returnTime, now);
        
        setTimeRemaining(secondsDiff);
        setIsOvertime(secondsDiff < 0);
      }
    };

    calculateTimeRemaining();
    const interval = setInterval(calculateTimeRemaining, 1000);

    return () => clearInterval(interval);
  }, [rental.expected_return_time]);

  const formatTime = (totalSeconds: number) => {
    const absSeconds = Math.abs(totalSeconds);
    const hours = Math.floor(absSeconds / 3600);
    const minutes = Math.floor((absSeconds % 3600) / 60);
    const seconds = absSeconds % 60;

    const pad = (num: number) => String(num).padStart(2, '0');

    return `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
  };

  const getTimeColor = () => {
    if (isOvertime) return "text-red-600 font-bold animate-pulse";
    // Consider timeRemaining in seconds for color logic if needed, e.g. timeRemaining <= 300 (for 5 minutes)
    if (!isOvertime && timeRemaining <= 300) return "text-red-600 font-bold animate-pulse";
    return "text-green-600 font-medium";
  };

  const getTimeLabel = () => {
    if (isOvertime) return "Tiempo excedido:";
    return "Tiempo restante:";
  };

  const startTime = rental.start_date ? format(new Date(rental.start_date), 'hh:mm a') : '';

  const handleCompleteRental = () => {
    setShowCompletionModal(true);
  };

  const handleConfirmCompletion = () => {
    completeMutation.mutate(rental.id);
  };

  return (
    <>
      <Card className="hover:shadow-lg transition-shadow">
        <CardHeader className="pb-3">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-2">
              <User className="h-5 w-5 text-bike-600" />
              <h3 className="font-semibold text-lg text-gray-800">
                {rental.customer?.name}
              </h3>
              {rental.customer?.phone && (
                <a
                  href={`tel:${rental.customer.phone}`}
                  className="text-bike-600 hover:text-bike-800 transition-colors"
                  title="Llamar al cliente"
                >
                  <Phone className="h-4 w-4" />
                </a>
              )}
            </div>
            <div className="text-right">
              <div>{startTime}</div>
            </div>
          </div>
        </CardHeader>
        
        <CardContent className="space-y-3">
          <div className="flex items-center gap-2">
            <Bike className="h-4 w-4 text-bike-600" />
            <Badge variant="outline" className="bg-bike-50 text-bike-700 border-bike-200">
              #{rental.bike?.number} {rental.bike?.type} - {rental.bike?.color}
            </Badge>
          </div>
          
          <div className="flex items-center gap-2">
            <Clock className="h-4 w-4 text-gray-500" />
            <span className="text-sm text-gray-600">{getTimeLabel()}</span>
            <span className={getTimeColor()}>
              {formatTime(timeRemaining)}
              {isOvertime && (
                <span>⚠️</span>
              )}
            </span><span className="text-sm font-semibold">{format(new Date(rental.expected_return_time), 'hh:mm a')}</span>
          </div>

          <div className="pt-3 border-t">
            <Button 
              onClick={handleCompleteRental}
              disabled={completeMutation.isPending}
              className="w-full bg-red-600 hover:bg-red-700 text-white"
              size="sm"
            >
              <CircleStop className="mr-2 h-4 w-4" />
              Finalizar Servicio
            </Button>
          </div>
        </CardContent>
      </Card>

      <RentalCompletionModal
        rental={rental}
        isOpen={showCompletionModal}
        onClose={() => setShowCompletionModal(false)}
        onConfirm={handleConfirmCompletion}
        isLoading={completeMutation.isPending}
        exchangeRate={exchangeRate}
      />
    </>
  );
};

export default ActiveRentalCard;
